<template>
  <article
    v-if="showHeader || mainElementMainImage || showSlider"
    :key="keyid"
    v-observe-visibility="{
      callback: handleScrollIntoView,
      once: true,
    }"
    :data-key="keyid"
    :data-banner-type="bannerType"
    :class="bemBlockMod(bannerClass)"
  >
    <BaseTitleSubtitle
      v-if="showHeader"
      :class="bemElement('title-subtitle')"
      :title="product.banner.mainElement.title"
      :subtitle="product.banner.mainElement.subtitle"
      :color="product.banner.mainElement.color"
    />

    <BaseLink
      v-if="mainElementMainImage"
      v-bind="product.banner.mainElement.link"
      :class="bemElement('link')"
    >
      <BaseImage
        :class="bemElement('pic')"
        :data-url="product.banner.mainElement.link.url"
        :image-classes="bemElement('image')"
        :image="getDefaultImageFromProductImageSet(mainElementMainImage)"
        :loading="forceMainImageToLoad ? 'eager' : null"
        :placeholder="
          getPlaceholderImageFromProductImageSet(mainElementMainImage)
        "
        :sizes="getSizes()"
        :srcset="getSrcsetFromProductImageSet(mainElementMainImage)"
        :use-title="false"
        @image-click="mainElementClick()"
      />
    </BaseLink>

    <BaseCarousel
      v-if="showSlider"
      :class="bemElement('slider')"
      :are-controls-shown="false"
      :items="product.banner.productSlider"
      :swiper-options="{
        allowTouchMove: true,
        loop: false,
        slidesPerView: 2.5,
        spaceBetween: 2,
      }"
    >
      <template #default="slotProps">
        <BaseProductTile
          :analytics="{
            location: analyticsConst.location.plpSliderBanner,
            position: gridIndex + (slotProps.index + 1) / 10,
          }"
          :are-carousel-controls-shown="false"
          :context="context"
          :force-main-image-to-load="true"
          :grid-index="gridIndex"
          :grid-type="gridType"
          :has-carousel="false"
          :hide-prices="hidePrices"
          :hide-product-names="hideProductNames"
          :hide-tags="hideTags"
          :keyid="`${slotProps.item.id}-${slotProps.index}`"
          :minimal="minimal"
          :product="slotProps.item"
          :show-swatches="false"
          :url="slotProps.item.selectedProductUrl || ''"
        />
      </template>
    </BaseCarousel>
  </article>
</template>

<script>
import { paramCase } from 'change-case';
import bemClassesMixin from '../mixins/bemClassesMixin';
import { productImageSetMixin } from '../mixins/productImageSetMixin';
import productPropBaseMixin from '../mixins/productPropBaseMixin';
import BaseCarousel from './BaseCarousel.vue';
import BaseImage from './BaseImage.vue';
import BaseLink from './BaseLink.vue';
import BaseProductTile from './BaseProductTile.vue';
import BaseTitleSubtitle from './BaseTitleSubtitle.vue';
import getAnalyticsEventBus from '../../common/getAnalyticsEventBus';
import getFrontConfig from '../../common/getFrontConfig';
import analyticsMixin from '../mixins/analyticsMixin';
import checkVisibilityMixin from '../mixins/checkVisibilityMixin';
import windowResizeMixin from '../mixins/windowResizeMixin';

const analyticsEventBus = getAnalyticsEventBus();

const {
  productImages: { imageSets },
  responsive: { bp_768: bp768 },
  vue: {
    BaseProductTile: {
      context: { plpSliderBanner },
    },
    TheProductList: { bannerTypes },
  },
} = getFrontConfig();

const BANNER_TYPE_TO_ANALYTICS = {
  [bannerTypes.plpMobileSlider]: {
    viewed: {
      eventName: analyticsEventBus.EVENTS.productListBannerCarouselViewed,
      payloadType:
        analyticsEventBus.EVENTS.PAYLOADS.productListBannerCarouselViewed.type
          .bannerCarousel,
    },
    clicked: {
      eventName: analyticsEventBus.EVENTS.productListBannerClicked,
      payloadType:
        analyticsEventBus.EVENTS.PAYLOADS.productListBannerClicked.type
          .bannerCarousel,
    },
  },
  [bannerTypes.plpFullImage]: {
    viewed: {
      eventName: analyticsEventBus.EVENTS.productListBannerViewed,
      payloadType:
        analyticsEventBus.EVENTS.PAYLOADS.productListBannerViewed.type
          .bannerImageFull,
    },
    clicked: {
      eventName: analyticsEventBus.EVENTS.productListBannerClicked,
      payloadType:
        analyticsEventBus.EVENTS.PAYLOADS.productListBannerClicked.type
          .bannerImageFull,
    },
  },
  [bannerTypes.plpDoubleImage]: {
    viewed: {
      eventName: analyticsEventBus.EVENTS.productListBannerViewed,
      payloadType:
        analyticsEventBus.EVENTS.PAYLOADS.productListBannerViewed.type
          .bannerImageDouble,
    },
    clicked: {
      eventName: analyticsEventBus.EVENTS.productListBannerClicked,
      payloadType:
        analyticsEventBus.EVENTS.PAYLOADS.productListBannerClicked.type
          .bannerImageDouble,
    },
  },
  [bannerTypes.plpSimpleImage]: {
    viewed: {
      eventName: analyticsEventBus.EVENTS.productListBannerViewed,
      payloadType:
        analyticsEventBus.EVENTS.PAYLOADS.productListBannerViewed.type
          .bannerImageSimple,
    },
    clicked: {
      eventName: analyticsEventBus.EVENTS.productListBannerClicked,
      payloadType:
        analyticsEventBus.EVENTS.PAYLOADS.productListBannerClicked.type
          .bannerImageSimple,
    },
  },
};

const SIZES = {
  PLP_GRID: '(min-width: 768px) 25vw, 50vw',
  PLP_DOUBLE_WIDTH: '100vw',
};

export default {
  name: 'TheGridBanner',

  components: {
    BaseCarousel,
    BaseImage,
    BaseLink,
    BaseProductTile,
    BaseTitleSubtitle,
  },

  mixins: [
    analyticsMixin,
    bemClassesMixin,
    checkVisibilityMixin,
    productPropBaseMixin,
    productImageSetMixin,
    windowResizeMixin,
  ],

  props: {
    context: {
      type: String,
      required: false,
      default: plpSliderBanner,
    },
    forceMainImageToLoad: {
      default: false,
      required: false,
      type: Boolean,
    },
    gridIndex: {
      type: Number,
      required: true,
    },
    gridType: {
      type: String,
      validator: (prop) =>
        ['crossSelling', 'productList', 'search'].includes(prop),
      required: false,
      default: null,
    },
    hidePrices: {
      type: Boolean,
      default: false,
      required: false,
    },
    hideProductNames: {
      type: Boolean,
      default: false,
      required: false,
    },
    hideTags: {
      type: Boolean,
      default: false,
      required: false,
    },
    keyid: {
      type: String,
      default: '',
      required: false,
    },
    minimal: {
      type: Boolean,
      default: false,
      required: false,
    },
  },

  data: () => ({
    dataChangeBreakpoint: bp768,
  }),

  computed: {
    bannerClass() {
      const listClass = {
        minimal: this.minimal,
      };
      const type = paramCase(this.bannerType);
      listClass[type] = true;
      return listClass;
    },
    bannerType() {
      return this.product.banner.type;
    },
    mainElementImageSet() {
      let imageSet = imageSets.PLP;
      if (this.context === plpSliderBanner) {
        imageSet = imageSets.PLAIN;
      }
      const bylImages =
        this.isBreakpointDesktop &&
        this.product.banner.mainElement.bylImagesDesktop
          ? this.product.banner.mainElement.bylImagesDesktop
          : this.product.banner.mainElement.bylImages;
      return this.getProductImageSets(imageSet, bylImages);
    },
    mainElementMainImage() {
      return this.mainElementImageSet?.[0];
    },
    showHeader() {
      return (
        (this.product.banner.mainElement.title ||
          this.product.banner.mainElement.subtitle) &&
        !this.minimal
      );
    },
    showSlider() {
      return this.product.banner.productSlider && !this.minimal;
    },
  },

  methods: {
    handleScrollIntoView(isVisible) {
      this.handleVisibilityChanged(isVisible);
      if (isVisible) {
        const analytics = BANNER_TYPE_TO_ANALYTICS[this.bannerType]?.viewed;
        const { eventName, payloadType: type } = analytics;
        const url = this.product.banner.mainElement?.link?.url;

        analyticsEventBus.publish(eventName, {
          position: this.gridIndex,
          type,
          url,
        });
      }
    },
    mainElementClick() {
      const url = this.product.banner.mainElement?.link?.url;
      if (url) {
        // Send info for Analytics
        const analytics = BANNER_TYPE_TO_ANALYTICS[this.bannerType]?.clicked;
        const { eventName, payloadType: type } = analytics;
        analyticsEventBus.publish(eventName, {
          url,
          type,
        });
      }
    },
    getSizes() {
      return this.bannerType === bannerTypes.plpSimpleImage
        ? SIZES.PLP_GRID
        : SIZES.PLP_DOUBLE_WIDTH;
    },
  },
};
</script>
